import type { EmbedProductResponse } from "../types/tree";

export interface FetchProductOptions {
  /** Base URL of the deployment (e.g. "https://dk3.3dconfig.dk"). Empty = same origin. */
  baseUrl?: string;
  /** Product GUID. */
  productId: string;
  /** Optional Unity target — when set, the response includes a viewer build URL. */
  target?: string;
  /** Unity deployment stage. */
  stage?: "Development" | "Staging" | "Production";
  signal?: AbortSignal;
}

/**
 * Fetches a single product's detail (surfaces + materials) plus an optional Unity
 * viewer build, in one call. The single-product analogue of fetchTree — there's no
 * tree to walk, the tool just renders the product's material picker straight away.
 *
 * Anonymous: product catalog content is read-only. Sent with credentials:"include"
 * so a dashboard cookie attaches when present (harmless for anonymous embeds).
 */
export async function fetchProduct(opts: FetchProductOptions): Promise<EmbedProductResponse> {
  const params = new URLSearchParams();
  if (opts.target) params.set("target", opts.target);
  if (opts.stage) params.set("stage", opts.stage);

  const base = (opts.baseUrl ?? "").replace(/\/+$/, "");
  const qs = params.toString();
  const url = `${base}/api/embed/product/${encodeURIComponent(opts.productId)}${qs ? "?" + qs : ""}`;

  const res = await fetch(url, {
    headers: { Accept: "application/json" },
    credentials: "include",
    signal: opts.signal,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`fetchProduct failed: ${res.status} ${res.statusText} ${text}`);
  }
  return (await res.json()) as EmbedProductResponse;
}
